import { Badge, Button, FormField, Modal, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReplenishmentSuggestion } from '@warehouse/shared';
import { ShoppingCart } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useListState } from '../../hooks/useListState';
import { usePermission } from '../../auth/auth-store';
import { formatQty } from '../../lib/format';
import { toastError, toastInfo, toastSuccess } from '../../lib/notify';
import { useLiveQuery } from '../../realtime/useLiveQuery';
import { replenishmentApi, replenishmentKeys } from './api';
import { daysCoverLabel, reasonTone, selectedLines } from './format';

/**
 * `/warehouse/replenishment` — the buyer's work list (spec §5.5).
 *
 * Every number on the row is shown, not just the verdict: on hand, what is
 * promised to restaurants, what is already on order, days of cover and the
 * thresholds that fired. A suggestion nobody can explain is a suggestion nobody
 * acts on.
 */
export function ReplenishmentPage(): ReactNode {
  const { t } = useTranslation('replenishment');
  const queryClient = useQueryClient();
  const canCreate = usePermission('warehouse.replenishment.createPo');

  const { state, setState, setFilter } = useListState({ defaultSort: 'articleNo' });
  const onlySuggested = state.filters?.onlySuggested !== 'false';

  const [selected, setSelected] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);
  const [expectedDate, setExpectedDate] = useState('');
  const [note, setNote] = useState('');

  const listState = useMemo(
    () => ({ ...state, filters: { ...state.filters, onlySuggested: onlySuggested ? 'true' : undefined } }),
    [state, onlySuggested],
  );

  const query = useQuery({
    queryKey: replenishmentKeys.suggestions(listState),
    queryFn: () => replenishmentApi.suggestions(listState),
  });
  // Stock moves and purchase orders both change the answer.
  useLiveQuery(['stock', 'purchase-orders', 'sales-orders'], ['replenishment']);

  const createPo = useMutation({
    mutationFn: () =>
      replenishmentApi.createDraftPo({
        lines: selectedLines(selected),
        expectedDate: expectedDate || null,
        note: note.trim() || null,
      }),
    onSuccess: async (result) => {
      toastSuccess(t('createPo.created', { count: result.orders.length }));
      if (result.skippedArticleIds.length > 0) {
        toastInfo(t('createPo.skipped', { count: result.skippedArticleIds.length }));
      }
      setSelected({});
      setCreating(false);
      await queryClient.invalidateQueries({ queryKey: ['replenishment'] });
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
    onError: (error) => {
      setCreating(false);
      toastError(error);
    },
  });

  const rows = query.data?.items ?? [];
  const meta = query.data?.meta;
  const selectedCount = Object.keys(selected).length;

  const columns: DataTableColumn<ReplenishmentSuggestion>[] = [
    {
      id: 'select',
      header: '',
      width: 'w-10',
      cell: (row) => (
        // No default supplier ⇒ "create draft PO" would fail server-side
        // (E_VALIDATION no_orderable_lines), so the row is not selectable and a
        // hint points at the fix: set a supplier on the article.
        row.canDraftPo ? (
          <input
            type="checkbox"
            className="size-4 rounded border-surface-border"
            aria-label={t('select.row', { article: row.articleNo })}
            checked={row.articleId in selected}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onChange={(event) => {
              setSelected((current) => {
                const next = { ...current };
                if (event.target.checked) next[row.articleId] = row.suggestedQtyBase;
                else delete next[row.articleId];
                return next;
              });
            }}
          />
        ) : (
          <span title={t('noSupplier.hint')} className="text-quaternary/60 text-xs">
            {t('noSupplier.short')}
          </span>
        )
      ),
    },
    {
      id: 'article',
      header: t('columns.article'),
      sortField: 'articleNo',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-ink">{row.articleName}</span>
          <span className="font-mono text-xs text-ink-muted">{row.articleNo}</span>
        </div>
      ),
    },
    {
      id: 'supplier',
      header: t('columns.supplier'),
      sortField: 'supplierName',
      hideBelow: 'lg',
      cell: (row) => row.supplierName ?? '—',
    },
    {
      id: 'onHand',
      header: t('columns.onHand'),
      align: 'right',
      sortField: 'onHandBase',
      cell: (row) => formatQty(row.onHandBase),
    },
    {
      id: 'demand',
      header: t('columns.demand'),
      align: 'right',
      hideBelow: 'md',
      cell: (row) => formatQty(row.demandBase),
    },
    {
      id: 'onOrder',
      header: t('columns.onOrder'),
      align: 'right',
      hideBelow: 'md',
      cell: (row) => formatQty(row.onOrderBase),
    },
    {
      id: 'available',
      header: t('columns.available'),
      align: 'right',
      sortField: 'availableBase',
      cell: (row) => formatQty(row.availableBase),
    },
    {
      id: 'daysCover',
      header: t('columns.daysCover'),
      align: 'right',
      sortField: 'daysCover',
      cell: (row) => daysCoverLabel(row.daysCover, t),
    },
    {
      id: 'reasons',
      header: t('columns.reasons'),
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.reasons.map((reason) => (
            <Badge key={reason} tone={reasonTone(reason)} size="sm">
              {t(`reasons.${reason}`)}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'suggested',
      header: t('columns.suggested'),
      align: 'right',
      sortField: 'suggestedQtyBase',
      cell: (row) => (
        <span className="font-medium tabular-nums text-ink">
          {formatQty(row.suggestedQtyBase, { unit: row.baseUnitCode })}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('page.title')}
        subtitle={t('page.subtitle')}
        actions={
          canCreate ? (
            <Button
              icon={<ShoppingCart className="size-4" />}
              disabled={selectedCount === 0}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('createPo.action')}
              {selectedCount > 0 ? ` (${selectedCount})` : ''}
            </Button>
          ) : null
        }
      />

      {meta ? (
        <p className="mb-4 text-xs text-ink-muted">
          {t('page.settings', {
            safety: meta.settings.safetyDays,
            window: meta.settings.consumptionWindowDays,
          })}{' '}
          · {t('page.suggestedCount', { count: meta.suggestedCount })}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.articleId}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('page.searchPlaceholder')}
        emptyTitle={t('page.empty')}
        emptyDescription={t('page.emptyHint')}
        filters={
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="size-4 rounded border-surface-border"
              checked={onlySuggested}
              onChange={(event) => {
                setFilter('onlySuggested', event.target.checked ? undefined : 'false');
              }}
            />
            {t('page.onlySuggested')}
          </label>
        }
      />

      {creating ? (
        <Modal
          open
          onClose={() => {
            setCreating(false);
          }}
          title={t('createPo.title')}
          width={520}
        >
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">{t('createPo.body')}</p>
            <p className="text-sm text-ink">{t('select.count', { count: selectedCount })}</p>
            <FormField
              label={t('createPo.expectedDate')}
              type="date"
              value={expectedDate}
              onChange={(event) => {
                setExpectedDate(event.target.value);
              }}
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink" htmlFor="replenishment-note">
                {t('createPo.note')}
              </label>
              <Textarea
                id="replenishment-note"
                rows={2}
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setCreating(false);
                }}
              >
                {t('createPo.action')}
              </Button>
              <Button
                loading={createPo.isPending}
                disabled={selectedCount === 0}
                onClick={() => {
                  createPo.mutate();
                }}
              >
                {t('createPo.submit')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
